
import { Union } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { union_type, list_type, class_type, tuple_type, option_type, int32_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { collect, empty as empty_1, map, item, mapIndexed, length, singleton, tail, cons, head, isEmpty } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { comparePrimitives, compare, compareArrays, curry2, equals } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { getCombinatorialOutputs, calculateCustomComponentsCombinatorialPaths, getNodeOrFail } from "./SynchronousUtils.js";
import { empty, FSharpSet__Add, FSharpSet__Contains } from "../fable_modules/fable-library-js.5.13.0/Set.js";
import { toText, join, printf, toFail } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { SimGraph_filterValues, SimGraph_toList, SimulationError, SimulationErrorType, SimGraph_tryFind } from "./SimGraphTypes.js";
import { FSharpResult$2, Result_Bind } from "../fable_modules/fable-library-js.5.13.0/Result.js";
import { value } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { isCustom, getCustomName } from "../Common/Helpers.js";

class DfsType extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["NoCycle", "Backtracking", "Cycle"];
    }
}

function DfsType_$reflection() {
    return union_type("SimulationGraphAnalyser.DfsType", [], DfsType, () => [[["Item", class_type("Microsoft.FSharp.Collections.FSharpSet`1", [tuple_type(int32_type, option_type(int32_type))])]], [["Item1", list_type(int32_type)], ["Item2", int32_type]], [["Item", list_type(int32_type)]]]);
}

function dfs(currNodeId, inputPortNumber, graph, visited, currStack, getCombOuts) {
    const exploreChildren = (visited_1_mut, currStack_1_mut, children_mut) => {
        exploreChildren:
        while (true) {
            const visited_1 = visited_1_mut, currStack_1 = currStack_1_mut, children = children_mut;
            if (!isEmpty(children)) {
                const matchValue = dfs(head(children)[0], head(children)[1], graph, visited_1, currStack_1, getCombOuts);
                switch (matchValue.tag) {
                    case 1: {
                        const cycleEnd = matchValue.fields[1];
                        const cycle = matchValue.fields[0];
                        if (equals(cycleEnd, currNodeId)) {
                            return new DfsType(/* Cycle */ 2, [cycle]);
                        }
                        else {
                            return new DfsType(/* Backtracking */ 1, [cons(currNodeId, cycle), cycleEnd]);
                        }
                    }
                    case 2:
                        return new DfsType(/* Cycle */ 2, [matchValue.fields[0]]);
                    default: {
                        visited_1_mut = matchValue.fields[0];
                        currStack_1_mut = currStack_1;
                        children_mut = tail(children);
                        continue exploreChildren;
                    }
                }
            }
            else {
                return new DfsType(/* NoCycle */ 0, [visited_1]);
            }
            break;
        }
    };
    const currNode = getNodeOrFail(graph, currNodeId);
    const inputPortNumber_1 = (currNode.Type.tag === 26) ? inputPortNumber : undefined;
    const curr = [currNodeId, inputPortNumber_1];
    const matchValue_3 = FSharpSet__Contains(currStack, curr);
    const matchValue_4 = FSharpSet__Contains(visited, curr);
    if (matchValue_3) {
        if (matchValue_4) {
            return new DfsType(/* Backtracking */ 1, [singleton(currNodeId), currNodeId]);
        }
        else {
            return toFail(printf("what? Node never visited but in the stack, while detecting cycle: %A"))(currNodeId);
        }
    }
    else if (matchValue_4) {
        return new DfsType(/* NoCycle */ 0, [visited]);
    }
    else {
        return exploreChildren(FSharpSet__Add(visited, curr), FSharpSet__Add(currStack, curr), getCombOuts(currNode, inputPortNumber_1));
    }
}

function calculateConnectionsAffected(connections, cycle) {
    const findConnection = (connections_1_mut, tupledArg_mut) => {
        findConnection:
        while (true) {
            const connections_1 = connections_1_mut, tupledArg = tupledArg_mut;
            const compIdFrom = tupledArg[0];
            const compIdTo = tupledArg[1];
            if (!isEmpty(connections_1)) {
                const conn = head(connections_1);
                const matchValue = equals(conn.Source.HostId, compIdFrom);
                const matchValue_1 = equals(conn.Target.HostId, compIdTo);
                let matchResult;
                if (matchValue) {
                    if (matchValue_1) {
                        matchResult = 0;
                    }
                    else {
                        matchResult = 1;
                    }
                }
                else {
                    matchResult = 1;
                }
                switch (matchResult) {
                    case 0:
                        return conn.Id;
                    default: {
                        connections_1_mut = tail(connections_1);
                        tupledArg_mut = [compIdFrom, compIdTo];
                        continue findConnection;
                    }
                }
            }
            else {
                return toFail(printf("what? Could not find connection among %A and %A"))(compIdFrom)(compIdTo);
            }
            break;
        }
    };
    if (length(cycle) < 2) {
        toFail(printf("what? Cycle with length less than 2: %A"))(cycle);
    }
    const list_1 = mapIndexed((i, compId) => [compId, item((i + 1) % length(cycle), cycle)], cycle);
    return map(curry2(findConnection)(connections), list_1);
}

function checkCombinatorialCycle(graph, connectionsOpt, inDependency, getCombOuts) {
    const checkGraphForest = (nodeIdsAndPNums_mut, visited_mut) => {
        checkGraphForest:
        while (true) {
            const nodeIdsAndPNums = nodeIdsAndPNums_mut, visited = visited_mut;
            if (!isEmpty(nodeIdsAndPNums)) {
                const matchValue = dfs(head(nodeIdsAndPNums)[0], head(nodeIdsAndPNums)[1], graph, visited, empty({
                    Compare: (x, y) => (compareArrays(x, y) | 0),
                }), getCombOuts);
                switch (matchValue.tag) {
                    case 2: {
                        const cycle = matchValue.fields[0];
                        let connectionsAffected;
                        if (connectionsOpt != null) {
                            const conns = connectionsOpt;
                            try {
                                connectionsAffected = calculateConnectionsAffected(conns, cycle);
                            }
                            catch (e) {
                                connectionsAffected = empty_1();
                            }
                        }
                        else {
                            connectionsAffected = empty_1();
                        }
                        const loop = join(" -> ", map((cid) => {
                            const matchValue_1 = SimGraph_tryFind(cid, graph);
                            if (matchValue_1 == null) {
                                return "?";
                            }
                            else {
                                return matchValue_1.Label;
                            }
                        }, cycle));
                        return new SimulationError(new SimulationErrorType(/* CycleDetected */ 14, [toText(printf("There is a combinational loop in this circuit: the highlighted components feed back into themselves without passing through any clocked component.\n\n%s -> (back to the start)\n\nSuch a circuit has no defined value - each output depends on itself - so it cannot be simulated, and real hardware built this way would oscillate. Break the loop: either remove one of the connections above, or put a clocked component (a D flip-flop or a register) into the path so that the feedback is delayed by one clock cycle."))(loop)]), inDependency, cycle, connectionsAffected);
                    }
                    case 1: {
                        const tupledArg = [matchValue.fields[0], matchValue.fields[1]];
                        return toFail(printf("what? Dfs should never terminate while backtracking: %A"))([tupledArg[0], tupledArg[1]]);
                    }
                    default: {
                        nodeIdsAndPNums_mut = tail(nodeIdsAndPNums);
                        visited_mut = matchValue.fields[0];
                        continue checkGraphForest;
                    }
                }
            }
            else {
                return undefined;
            }
            break;
        }
    };
    const visited_2 = empty({
        Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
    });
    return checkGraphForest(collect((tupledArg_1) => {
        const id = tupledArg_1[0];
        const matchValue_2 = tupledArg_1[1].Type;
        if (matchValue_2.tag === 26) {
            return mapIndexed((i, _arg) => [id, i], matchValue_2.fields[0].InputLabels);
        }
        else {
            return singleton([id, 0]);
        }
    }, SimGraph_toList(graph)), visited_2);
}

function recursivelyCheckCombinatorialCycles(currGraph, connectionsOpt, dependencyName, alreadyChecked, getCombOuts) {
    const iterateChildren = (alreadyChecked_1, children) => {
        if (!isEmpty(children)) {
            const child = head(children)[1];
            return Result_Bind((alreadyChecked_2) => iterateChildren(alreadyChecked_2, tail(children)), recursivelyCheckCombinatorialCycles(value(child.CustomSimulationGraph), undefined, getCustomName(child.Type), alreadyChecked_1, getCombOuts));
        }
        else {
            return new FSharpResult$2(/* Ok */ 0, [alreadyChecked_1]);
        }
    };
    if (FSharpSet__Contains(alreadyChecked, dependencyName)) {
        return new FSharpResult$2(/* Ok */ 0, [alreadyChecked]);
    }
    else {
        return Result_Bind((alreadyChecked_4) => {
            const _arg = checkCombinatorialCycle(currGraph, connectionsOpt, (connectionsOpt != null) ? undefined : dependencyName, getCombOuts);
            if (_arg != null) {
                return new FSharpResult$2(/* Error */ 1, [_arg]);
            }
            else {
                return new FSharpResult$2(/* Ok */ 0, [alreadyChecked_4]);
            }
        }, iterateChildren(FSharpSet__Add(alreadyChecked, dependencyName), SimGraph_toList(SimGraph_filterValues((comp) => isCustom(comp.Type), currGraph))));
    }
}

/**
 * Analyse a SimulationGraph and return any error (or None).
 * The SimulationGraph should be fully merged with its dependency, so this
 * function has to be called after the dependency merger has finished.
 * This function assumes that there are no cyclic dependencies.
 */
export function analyseSimulationGraph(diagramName, graph, connections) {
    const matchValue = calculateCustomComponentsCombinatorialPaths(diagramName, graph);
    if (matchValue != null) {
        const cccp = matchValue;
        const _arg = recursivelyCheckCombinatorialCycles(graph, connections, diagramName, empty({
            Compare: (x, y) => (comparePrimitives(x, y) | 0),
        }), (node, inputPortNumberOpt) => getCombinatorialOutputs(cccp, node, inputPortNumberOpt));
        if (_arg.tag === 1) {
            return _arg.fields[0];
        }
        else {
            return undefined;
        }
    }
    else {
        return toFail(printf("what? calculateCustomComponentsCombinatorialPaths returned None whithin analyseSimulationGraph. This should never happen"));
    }
}

